import { defineComponent as _defineComponent } from 'vue'
import { __ } from '@@/bits/intl'
import { getEditorObject } from '@@/bits/tldraw/editor'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import type { Anchor } from '@@/library/v4/components/OzBasePopover.vue'
import OzBasePopover, { OzBasePopoverColors } from '@@/library/v4/components/OzBasePopover.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceWindowMobileNotchStore } from '@@/pinia/surface_window_mobile_notch'
import { useWhiteboardStore } from '@@/pinia/whiteboard'
import { useWhiteboardFramePreviewStore } from '@@/pinia/whiteboard_frame_preview'
import { useWhiteboardPermissionsStore } from '@@/pinia/whiteboard_permissions'
import WhiteboardFramePreview from '@@/vuecomponents/WhiteboardFramePreview.vue'
import { autoPlacement, offset } from '@floating-ui/vue'
import { storeToRefs } from 'pinia'
import { react, type TLPageId } from 'tldraw'
import { computed, nextTick, onMounted, ref } from 'vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'WhiteboardFrameBar',
  props: {
    maxHeight: null,
    minHeight: null,
    xFrameContextMenu: { type: Boolean }
  },
  emits: ["show-frame-context-menu"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'show-frame-context-menu', anchor: Anchor, frameId: string): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  maxHeight: number
  minHeight: number
  xFrameContextMenu: boolean
};

const surfaceStore = useSurfaceStore()
const whiteboardStore = useWhiteboardStore()
const surfaceContainerSizeStore = useSurfaceContainerSizeStore()

const { isContainerSmallerThanTabletPortrait } = storeToRefs(surfaceContainerSizeStore)
const { isLoadingPortraitMobileLayout } = storeToRefs(whiteboardStore)




/**
 * TOOLTIP
 */
const xTooltip = ref(false)
const buttonRef = ref<typeof OzBaseButton | null>(null)
const buttonEl = computed(() => buttonRef.value?.$el)
const tooltipTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
function handleButtonHover(): void {
  // Cancellable delayed tooltip
  tooltipTimeout.value = setTimeout(() => {
    xTooltip.value = true
  }, 500)
}

function handleButtonLeave(): void {
  // Clear tooltip timeout
  if (tooltipTimeout.value) {
    clearTimeout(tooltipTimeout.value)
    tooltipTimeout.value = null
  }
  xTooltip.value = false
}

/**
 * VISUALS
 */

// COLOR SCHEME
// Currently follow background luminance of surface (based on walllpaper)
const colorScheme = computed((): 'dark' | 'light' => {
  return surfaceStore.backgroundLuminance
})

const helperText = computed(() => {
  if (whiteboardStore.hasReachedPlanFrameLimitAndCannotUpgrade) {
    return __('At %{whiteboardFrameLimit} card limit for this sandbox', {
      whiteboardFrameLimit: surfaceStore.whiteboardFrameLimit,
    })
  }
  return whiteboardStore.isSingleFrame ? __('Add a new card') : __('Add a card below the current card')
})
const containerRef = ref<HTMLElement | null>(null)
const height = ref<number>(props.minHeight ?? 100)

// Animate height when new frame is added
onMounted(() => {
  if (isContainerSmallerThanTabletPortrait.value) {
    return
  }
  nextTick(() => {
    height.value = Math.max(containerRef.value?.scrollHeight ?? 0, props.minHeight ?? 100)
    if (!containerRef.value) return
    const observer = new MutationObserver(() => {
      if (containerRef.value) {
        height.value = containerRef.value.scrollHeight
      }
    })
    observer.observe(containerRef.value, { childList: true, subtree: true })
  })
})

/** FRAME LOGIC */
const { frames, currentFrameId } = storeToRefs(whiteboardStore)

function addFrame(): void {
  whiteboardStore.addFrameBelow(whiteboardStore.currentFrameId!)
}

// Switch Frame
function switchFrame(pageId: string): void {
  const editor = getEditorObject()
  if (!editor) return
  editor.setCurrentPage(pageId as TLPageId)
}

/** FRAME PREVIEWS */
const whiteboardFramePreviewStore = useWhiteboardFramePreviewStore()
const { framePreviews } = storeToRefs(whiteboardFramePreviewStore)
// Mount life-cycle logic:
// Update initial frame previews
// Listen to document changes and trigger preview update
onMounted(() => {
  const editor = getEditorObject()
  if (editor == null) {
    return
  }

  whiteboardFramePreviewStore.updatePreviewForAllFrames()

  // mark frame as having changes for preview reloading
  editor.store.listen(whiteboardFramePreviewStore.debouncedHandlePageChange, { scope: 'document', source: 'all' })

  // Update frames with changes when editor.getPath() changes
  react('update pending frame previews', () => {
    if (
      editor == null ||
      !editor.isInAny(
        'draw.idle',
        'select.idle',
        'highlight.idle',
        'text.idle',
        'padlet-draw-eraser.idle',
        'laser.idle',
        'note.idle',
        'geo.idle',
        'interact',
      )
    ) {
      return
    }
    if (whiteboardFramePreviewStore.framesWithPreviewChanges.length === 0) {
      return
    }
    whiteboardFramePreviewStore.conditionallyUpdateFramesWithChanges()
  })
})

/**
 * FRAME CONTEXT MENU
 */

function showContextMenu(e: PointerEvent | MouseEvent, frameId: string) {
  const buttonAnchor = e.currentTarget as Element
  const rect = buttonAnchor.getBoundingClientRect()
  emit('show-frame-context-menu', { x: rect.right, y: rect.top }, frameId)
}

/** PERMISSION */
const whiteboardPermissionsStore = useWhiteboardPermissionsStore()

const surfaceWindowMobileNotchStore = useSurfaceWindowMobileNotchStore()

return { __sfc: true,surfaceStore, whiteboardStore, surfaceContainerSizeStore, isContainerSmallerThanTabletPortrait, isLoadingPortraitMobileLayout, props, emit, xTooltip, buttonRef, buttonEl, tooltipTimeout, handleButtonHover, handleButtonLeave, colorScheme, helperText, containerRef, height, frames, currentFrameId, addFrame, switchFrame, whiteboardFramePreviewStore, framePreviews, showContextMenu, whiteboardPermissionsStore, surfaceWindowMobileNotchStore, OzBaseButton, OzBasePopover, OzBasePopoverColors, OzIcon, WhiteboardFramePreview, autoPlacement, offset }
}

})